'use client';

/**
 * EntityDetailModal Component
 * Modal for viewing and editing entity details with tabbed interface for
 * costs, documentation, ownership, reliability, and ArchiMate mappings
 */

import { useState, useEffect } from 'react';
import type { Entity, EntityStatus, EntityMetadata, EntityReliability, DependencyMetrics } from '@/lib/command-center/types';
import { ENTITY_TYPE_LABELS } from '@/lib/command-center/types';
import ArchiMateMappingSection from './ArchiMateMappingSection';
import EntityMetadataFields from './EntityMetadataFields';
import { CostTracker } from './CostTracker';
import { DocumentationPanel } from './DocumentationPanel';
import { OwnershipPanel } from './OwnershipPanel';
import { ReliabilityScoreCard } from './ReliabilityScoreCard';

type TabId = 'details' | 'costs' | 'docs' | 'ownership' | 'reliability';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'details', label: 'Details', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'ownership', label: 'Ownership', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  { id: 'costs', label: 'Costs', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'docs', label: 'Docs', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { id: 'reliability', label: 'Reliability', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
];

interface EntityDetailModalProps {
  entity: Entity;
  workspaceId: string;
  onClose: () => void;
  onDelete?: () => void;
  onUpdated?: (entity: Entity) => void;
}

// Status badge colors
const STATUS_COLORS: Record<EntityStatus, string> = {
  active: 'bg-green-900/50 text-green-400 border-green-500/30',
  archived: 'bg-gray-900/50 text-gray-400 border-gray-500/30',
  deprecated: 'bg-red-900/50 text-red-400 border-red-500/30',
};

// Status options for dropdown
const STATUS_OPTIONS: { value: EntityStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'deprecated', label: 'Deprecated' },
];

export default function EntityDetailModal({
  entity,
  workspaceId,
  onClose,
  onDelete,
  onUpdated,
}: EntityDetailModalProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>('details');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState(entity.name);
  const [description, setDescription] = useState(entity.description || '');
  const [status, setStatus] = useState<EntityStatus>(entity.status);
  const [tagsInput, setTagsInput] = useState(entity.tags?.join(', ') || '');
  const [metadata, setMetadata] = useState<EntityMetadata>(entity.metadata || {});

  // Reliability data state
  const [reliability, setReliability] = useState<EntityReliability | null>(null);
  const [dependencies, setDependencies] = useState<DependencyMetrics | null>(null);
  const [reliabilityLoading, setReliabilityLoading] = useState(false);

  const statusClass = STATUS_COLORS[entity.status] || STATUS_COLORS.active;

  // Fetch reliability data when tab is activated
  useEffect(() => {
    if (activeTab === 'reliability' && !reliability && !reliabilityLoading) {
      fetchReliabilityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchReliabilityData = async () => {
    setReliabilityLoading(true);
    try {
      const [relRes, depRes] = await Promise.all([
        fetch(`/api/secure/command-center/entities/${entity.id}/reliability`),
        fetch(`/api/secure/command-center/entities/${entity.id}/dependencies?include_metrics=true`),
      ]);

      if (relRes.ok) {
        const relData = await relRes.json();
        setReliability(relData.reliability || null);
      }

      if (depRes.ok) {
        const depData = await depRes.json();
        setDependencies(depData.metrics || null);
      }
    } catch (err) {
      console.error('Failed to fetch reliability data:', err);
    } finally {
      setReliabilityLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Parse tags from comma-separated string
  const parseTags = (input: string): string[] => {
    return input
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
  };

  // Cancel editing and reset form
  const handleCancel = () => {
    setName(entity.name);
    setDescription(entity.description || '');
    setStatus(entity.status);
    setTagsInput(entity.tags?.join(', ') || '');
    setMetadata(entity.metadata || {});
    setError(null);
    setIsEditing(false);
  };

  // Save changes
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/secure/command-center/entities/${entity.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          status,
          tags: parseTags(tagsInput),
          metadata,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update entity');
      }

      const updated = await response.json();

      // Notify parent of update
      if (onUpdated) {
        onUpdated(updated);
      }

      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={isEditing ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative bg-surface border border-border rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-muted shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-xl font-semibold text-foreground bg-background border border-border rounded-lg px-3 py-1 w-full focus:border-accent focus:outline-none"
                placeholder="Entity name"
              />
            ) : (
              <>
                <h2 className="text-xl font-semibold text-foreground truncate">{entity.name}</h2>
                <span className={`px-2 py-0.5 text-xs rounded border shrink-0 ${statusClass}`}>
                  {entity.status}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 ml-2">
            {!isEditing && activeTab === 'details' && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-foreground-tertiary hover:text-accent p-1"
                title="Edit entity"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            <button
              onClick={isEditing ? handleCancel : onClose}
              className="text-foreground-tertiary hover:text-foreground p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs - only show when not editing */}
        {!isEditing && (
          <div className="flex gap-1 px-4 pt-3 pb-0 border-b border-border-muted shrink-0 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-background text-accent border-t border-l border-r border-border'
                    : 'text-foreground-tertiary hover:text-foreground hover:bg-background/50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg shrink-0">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Details Tab */}
          {(activeTab === 'details' || isEditing) && (
            <div className="p-4 space-y-4">
              {/* Entity Type (read-only) */}
              <div>
                <label className="block text-sm text-foreground-tertiary mb-1">Type</label>
                <p className="text-foreground font-medium">
                  {ENTITY_TYPE_LABELS[entity.entity_type] || entity.entity_type}
                </p>
              </div>

              {/* Status */}
              {isEditing ? (
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as EntityStatus)}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:border-accent focus:outline-none"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* Description */}
              <div>
                <label className="block text-sm text-foreground-tertiary mb-1">Description</label>
                {isEditing ? (
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:border-accent focus:outline-none resize-none"
                    placeholder="Add a description..."
                  />
                ) : entity.description ? (
                  <p className="text-foreground">{entity.description}</p>
                ) : (
                  <p className="text-foreground-muted italic">No description</p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm text-foreground-tertiary mb-1">Tags</label>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:border-accent focus:outline-none"
                      placeholder="Enter tags separated by commas"
                    />
                    <p className="text-foreground-muted text-xs mt-1">Separate tags with commas</p>
                  </>
                ) : entity.tags && entity.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {entity.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-background text-foreground-secondary text-sm rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-foreground-muted italic">No tags</p>
                )}
              </div>

              {/* External Source (read-only) */}
              {entity.external_source && (
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-1">Source</label>
                  <p className="text-foreground capitalize">{entity.external_source}</p>
                </div>
              )}

              {/* Type-specific metadata fields */}
              <EntityMetadataFields
                entityType={entity.entity_type}
                metadata={isEditing ? metadata : entity.metadata}
                onChange={setMetadata}
                isEditing={isEditing}
              />

              {/* ArchiMate Mappings (only show when not editing) */}
              {!isEditing && (
                <div className="pt-4 border-t border-border-muted">
                  <ArchiMateMappingSection entityId={entity.id} entityName={entity.name} />
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border-muted">
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-1">Created</label>
                  <p className="text-foreground text-sm">{formatDate(entity.created_at)}</p>
                </div>
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-1">Last Updated</label>
                  <p className="text-foreground text-sm">{formatDate(entity.updated_at)}</p>
                </div>
              </div>

              {/* ID */}
              <div className="pt-4 border-t border-border-muted">
                <label className="block text-sm text-foreground-tertiary mb-1">Entity ID</label>
                <code className="text-foreground-tertiary text-xs bg-background px-2 py-1 rounded font-mono">
                  {entity.id}
                </code>
              </div>
            </div>
          )}

          {/* Ownership Tab */}
          {activeTab === 'ownership' && !isEditing && (
            <div className="p-4">
              <OwnershipPanel
                entityId={entity.id}
                entityName={entity.name}
                workspaceId={workspaceId}
              />
            </div>
          )}

          {/* Costs Tab */}
          {activeTab === 'costs' && !isEditing && (
            <div className="p-4">
              <CostTracker
                entityId={entity.id}
                entityName={entity.name}
                workspaceId={workspaceId}
              />
            </div>
          )}

          {/* Documentation Tab */}
          {activeTab === 'docs' && !isEditing && (
            <div className="p-4">
              <DocumentationPanel
                entityId={entity.id}
                entityName={entity.name}
                workspaceId={workspaceId}
              />
            </div>
          )}

          {/* Reliability Tab */}
          {activeTab === 'reliability' && !isEditing && (
            <div className="p-4">
              {reliabilityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                </div>
              ) : (
                <ReliabilityScoreCard
                  entityId={entity.id}
                  entityName={entity.name}
                  reliability={reliability}
                  dependencies={dependencies}
                  onRefresh={fetchReliabilityData}
                />
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-4 border-t border-border-muted shrink-0 bg-surface">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-foreground-tertiary hover:text-foreground transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              {onDelete ? (
                <button
                  onClick={onDelete}
                  className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
                >
                  Delete
                </button>
              ) : (
                <div />
              )}
              <button
                onClick={onClose}
                className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
